/* Spielerliste: laden, suchen, auswählen, speichern.
 *
 * Zustand im Modulbereich, von allen Komponenten geteilt.
 */

import { ref, computed } from 'vue';
import * as api from '../supabase/players.js';
import * as contractsApi from '../supabase/contracts.js';
import * as transfersApi from '../supabase/transfers.js';
import * as clubsApi from '../supabase/footballClubs.js';
import * as storageApi from '../supabase/storage.js';
import * as notificationsApi from '../supabase/notifications.js';
import { deletePlayerFlow, NON_PLAYER_FOLDERS } from '../domain/playerDeletion.js';
import { clearUploads } from './useUploadTracker.js';
import { useNotifications } from './useNotifications.js';
import { useAuth } from './useAuth.js';
import { useFootballClubs } from './useFootballClubs.js';
import { playerClub } from '../domain/footballClub.js';
import { attachContract, detachContract, validateContract } from '../domain/contracts.js';
import { isConfigured } from '../supabase/client.js';
import { fullName } from '../domain/format.js';
import { groupByPosition, groupByLetter } from '../domain/positions.js';
import {
  squadYears, effectiveYear, filterSquad, positionCountsFor, yearCountsFor, groupByAge, activeFilterCount as countActiveFilters,
} from '../domain/squadFilter.js';
import { AGE_GROUP_ALL, ageGroupCounts, ageGroupOf, isAgeGroup } from '../domain/ageGroups.js';
import { createListView } from './useListView.js';
import { isOutOfSquad } from '../domain/status.js';
import { ensurePlayerShape } from '../domain/playerShape.js';
import { todayIso } from '../domain/birthDate.js';
import { playersReadSource, PLAYERS_SOURCE_PUBLIC, PLAYERS_SOURCE_STAFF } from '../domain/playerPublic.js';
import { makeContext, resolveStep, canStepProfile, positionOf } from '../domain/profileNav.js';

const players = ref([]);
const loading = ref(false);
const error = ref(null);
const query = ref('');
const openPlayerId = ref(null);
/* Von außen gewünschter Reiter beim nächsten Öffnen — z. B. aus einer
   Benachrichtigung heraus (NotificationBell.vue). PlayerPanel.vue liest das
   beim Öffnen einmal aus und fällt ohne Vorgabe auf seinen eigenen
   Standard-Reiter zurück. */
const requestedTab = ref(null);
/* Listenkontext des offenen Profils: IDs in der Reihenfolge, in der die Liste sie beim Öffnen zeigte
   (domain/profileNav.js). Eingefroren, solange das Profil offen ist; null = geöffnet ohne Liste
   (Benachrichtigung, Direktöffnung) → kein Wechsel zum Nachbarn. Nur Speicher, nichts wird gespeichert. */
const profileContext = ref(null);
/* Filter (wer wird angezeigt) und Sortierung (in welcher Reihenfolge) sind
   zwei getrennte Zustände — Entscheidung Bastian, 2026-09-17. Gehören hierher
   wie die Suche: geteilter Listenzustand, kein lokaler Komponentenzustand. */
/* Je Seite eine eigene Instanz (useListView.js): Kaderseite und Transfers-Seite sind unabhängig. */
const squadView = createListView();
const transferView = createListView();
const { positionFilter, sortMode } = squadView;
/* Jahrgang ('all' | '2008' …) und Richtung der Altersortierung. Wie Position und Suche nur im Speicher, nicht
   dauerhaft gemerkt (Entscheidung Bastian, 2026-10-05). Die Richtung bleibt beim Wechsel der Sortierung
   erhalten, solange die Seite offen ist. */
const yearFilter = ref('all');
const { ageDirection } = squadView;
/* Altersklasse ('all' | 'U17' | 'U19'): einfache Umschaltung, gilt zusammen mit Suche, Position und Jahrgang (UND).
   Nur im Speicher wie die anderen Filter. */
const { ageGroupFilter } = squadView;
let loaded = false;
let loadPromise = null;
/* Aus welcher Quelle die Liste geladen wurde ('players' oder 'players_public', siehe
   domain/playerPublic.js) und wann — für den Wechsel der Rolle und das stille Auffrischen. */
let loadedSource = null;
let lastLoadedAt = 0;
let quietReloading = false;
const contractsError = ref(null);

/* Gäste lesen nur öffentlich und schreiben nie; ihre Liste wird still nachgeladen, wenn die
   App wieder in den Vordergrund kommt und der letzte Stand älter ist als das hier. */
const READER_REFRESH_MS = 60 * 1000;

export function usePlayers() {
  const { can, role, whenRoleResolved } = useAuth();
  const contractsAllowed = () => can('contracts', 'read');
  const { byId: clubById } = useFootballClubs();
  const openPlayer = computed(
    () => players.value.find((p) => p.id === openPlayerId.value) || null
  );

  const matching = computed(() => {
    const q = query.value.trim().toLowerCase();
    if (!q) return players.value;
    return players.value.filter((p) => {
      const haystack = [
        fullName(p), p.pos, p.pos2, playerClub(clubById.value, p).name,
        p.nationality, String(p.jerseyNumber || ''),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  });

  /* Drei getrennte Listen, wie in der ursprünglichen Fassung: der aktive
     Kader, die abgegebenen und die freigegebenen Spieler. Jede bekommt einen
     eigenen Abschnitt mit Überschrift. */
  const squad = computed(() => matching.value.filter((p) => !isOutOfSquad(p.status)));

  /* Jahrgänge des GANZEN Kaders (nicht des Suchtreffers): Die Knöpfe bleiben beim Tippen stehen und
     verschwinden nur, wenn der letzte Spieler eines Jahrgangs aus dem Kader geht. Regeln: domain/squadFilter.js. */
  const years = computed(() => squadYears(players.value.filter((p) => !isOutOfSquad(p.status))));
  /* Der gewählte Jahrgang, sofern es ihn noch gibt — sonst 'all' */
  const activeYear = computed(() => effectiveYear(yearFilter.value, years.value));

  /* Anzahl je Mannschaftsteil und je Jahrgang für die Filterknöpfe — bezogen auf Suchtreffer UND den
     jeweils anderen Filter, damit eine Zahl dem entspricht, was man nach dem Klick sieht. */
  const positionCounts = computed(() => positionCountsFor(squad.value, activeYear.value, ageGroupFilter.value));
  const yearCounts = computed(() => yearCountsFor(squad.value, years.value, positionFilter.value, ageGroupFilter.value));
  /* Zahlen der Altersklassen-Knöpfe: bezogen auf Suche, Position und Jahrgang, nicht auf die Klasse selbst */
  const ageGroupCountsNow = computed(() =>
    ageGroupCounts(filterSquad(squad.value, { position: positionFilter.value, year: activeYear.value }))
  );
  /* Hat irgendein Spieler im Kader eine Altersklasse? Ohne sie (SQL für Staff noch nicht ausgeführt, noch nichts
     gepflegt) zeigt die Liste für Leser keinen toten Filter. */
  const anyAgeGroup = computed(() => squad.value.some((p) => ageGroupOf(p)));

  /* Zahl der aktiven Filter (Position, Jahrgang, Altersklasse) für den Knopf "Filter" — Sortierung und Suche zählen nicht */
  const activeFilterCount = computed(() =>
    countActiveFilters({ position: positionFilter.value, year: activeYear.value, ageGroup: ageGroupFilter.value })
  );
  /** Alle Filter auf Standard. Die Suche und die Sortierung bleiben stehen. */
  function resetFilters() {
    positionFilter.value = 'all';
    yearFilter.value = 'all';
    ageGroupFilter.value = AGE_GROUP_ALL;
  }

  const filteredSquad = computed(() =>
    filterSquad(squad.value, { position: positionFilter.value, year: activeYear.value, ageGroup: ageGroupFilter.value })
  );

  const squadSections = computed(() => {
    if (sortMode.value === 'az') return groupByLetter(filteredSquad.value);
    if (sortMode.value === 'age') return groupByAge(filteredSquad.value, ageDirection.value);
    return groupByPosition(filteredSquad.value);
  });

  /* Freigegeben hängt bewusst an `players`, nicht an `matching`: Der Abschnitt ist zuklappbar — hinge er
     an der Suche, würde sich der Inhalt eines zugeklappten Abschnitts beim Tippen ändern und die Anzahl
     in der Kopfzeile mitspringen. Entscheidung Bastian, 2026-09-18.
     Die Transferierten haben seit 2026-10-06 eine eigene Seite (TransferredList.vue) und folgen dort der
     Suche: `transferred` ist die Gesamtzahl, `transferredMatching` die Suchtreffer. */
  const transferred = computed(() => players.value.filter((p) => p.status === 'transferred'));
  const transferredMatching = computed(() => matching.value.filter((p) => p.status === 'transferred'));

  const released = computed(() => players.value.filter((p) => p.status === 'released'));

  async function load() {
    if (!isConfigured) {
      error.value = 'notConnected';
      return;
    }
    loading.value = true;
    error.value = null;
    try {
      /* Erst wenn die Rolle feststeht: Sie entscheidet über die Quelle. Vorher fiele sie auf
         guest zurück, und ein Admin bekäme kurz den Gast-Lesepfad. */
      await whenRoleResolved();
      const source = playersReadSource(role.value);
      /* Staff liest auch die Transferhistorie aus der Ansicht ohne Freitext und Ersteller (supabase/transfers.js). */
      transfersApi.setTransfersSource(source === PLAYERS_SOURCE_STAFF ? 'staff' : 'full');
      players.value = await api.loadPlayers({ source });
      loadedSource = source;
      lastLoadedAt = Date.now();
      loaded = true;
    } catch (e) {
      error.value = e.message || 'loadFailed';
    } finally {
      loading.value = false;
    }
    await attachContracts();
  }

  async function loadOnce() {
    if (!loadPromise) loadPromise = load();
    await loadPromise;
  }

  /**
   * Nach dem Abmelden alles verwerfen: Der nächste Nutzer darf nichts aus der Liste des
   * vorigen sehen (anderer Lesepfad, andere Felder), und loadOnce lädt wieder neu.
   */
  function reset() {
    players.value = [];
    openPlayerId.value = null;
    profileContext.value = null;
    loaded = false;
    loadPromise = null;
    loadedSource = null;
    lastLoadedAt = 0;
    error.value = null;
    contractsError.value = null;
  }

  /**
   * Hat sich die Rolle so geändert, dass die geladene Liste aus der falschen Quelle stammt
   * (z. B. ein Gast wird zu staff befördert, während die App offen ist)? Dann neu laden.
   */
  async function syncSource() {
    if (!loaded) return;
    if (playersReadSource(role.value) !== loadedSource) {
      loadPromise = load();
      await loadPromise;
    }
  }

  /**
   * Für Gäste: Liste still auffrischen, wenn die App wieder sichtbar wird oder den Fokus
   * bekommt. Es gibt keine Live-Aktualisierung der Spieler (Realtime hängt nur an
   * Benachrichtigungen und Zugangsanfragen), die Liste war also nie live. Gäste schreiben nie,
   * deshalb ist ein Austausch der Liste ohne ungespeicherte Änderungen möglich. Für alle anderen
   * Rollen passiert hier nichts: ihre Panels können ungespeicherte Änderungen halten.
   * Ohne Ladeanzeige, ohne Fehlermeldung (der alte Stand bleibt stehen).
   */
  async function refreshForReader() {
    if (!loaded || loadedSource !== PLAYERS_SOURCE_PUBLIC || quietReloading) return;
    if (Date.now() - lastLoadedAt < READER_REFRESH_MS) return;
    quietReloading = true;
    try {
      players.value = await api.loadPlayers({ source: PLAYERS_SOURCE_PUBLIC });
      lastLoadedAt = Date.now();
    } catch {
      /* alter Stand bleibt; nächster Versuch beim nächsten Vordergrund */
    } finally {
      quietReloading = false;
    }
  }

  /* Vertragsdaten (Stufe 3, Teil 2) an die Spieler hängen — nur für Rollen mit
     Recht darauf; für alle anderen wird gar nicht erst abgefragt. Ein Fehler
     hier lässt die Spielerliste unberührt: die Spieler bekommen dann KEIN
     `contract`, und gespeichert wird für sie nichts (siehe save). */
  async function attachContracts() {
    if (!loaded || !contractsAllowed()) return;
    try {
      const { contracts, files } = await contractsApi.loadContracts();
      for (const p of players.value) attachContract(p, contracts.get(p.id), files.get(p.id) || []);
      contractsError.value = null;
    } catch (e) {
      contractsError.value = e.message || 'loadFailed';
    }
  }

  /** Vertragsdaten aus dem Speicher nehmen (Rolle hat das Recht nicht mehr). */
  function dropContracts() {
    players.value.forEach(detachContract);
  }

  /** Beim Rollenwechsel/Anmelden: laden, wenn erlaubt, sonst wegnehmen. */
  async function syncContracts() {
    if (contractsAllowed()) {
      if (loadPromise) await loadPromise;
      if (players.value.some((p) => p.contract === undefined)) await attachContracts();
    } else {
      dropContracts();
    }
  }

  /* Speichern in zwei Schritten: erst die Zeile des Spielers, dann die
     Vertragsdaten (eigene Tabellen). Schlägt der zweite Schritt fehl, wirft
     save() — das Panel zeigt dann den Fehler und NICHT "Gespeichert", und der
     Schnappschuss bleibt alt, sodass "Speichern" weiter aktiv ist. Ein neu
     angelegter Spieler behält dabei seine neue id, ein zweiter Versuch legt
     ihn nicht doppelt an. */
  async function save(player) {
    error.value = null;
    try {
      /* Gäste schreiben nie: weder über die Oberfläche (dort fehlen die Knöpfe) noch hier. Die
         Datenbank verweigert es ebenfalls (players_update/insert). */
      if (!can('players', 'write') || player._publicOnly || player._staffView) throw new Error('notAllowed');
      const withContracts = contractsAllowed() && player.contract !== undefined;
      if (withContracts && validateContract(player.contract).length) throw new Error('contractInvalid');

      const saved = await api.savePlayer(player);
      const i = players.value.findIndex((p) => p.id === saved.id);
      if (i >= 0) players.value[i] = saved;
      else players.value.push(saved);
      const entry = players.value.find((p) => p.id === saved.id);

      if (withContracts) {
        entry.contract = player.contract;
        entry.removedContractFiles = player.removedContractFiles || [];
        entry.contractFiles = player.contractFiles || [];
        entry.contractFiles = await contractsApi.saveContracts(entry);
        entry.removedContractFiles = [];
      }
      return entry;
    } catch (e) {
      error.value = e.message || 'saveFailed';
      throw e;
    }
  }

  /**
   * Altersklasse für mehrere Spieler setzen (Auswahlleiste, nur Rollen mit Schreibrecht auf players). `value` ist
   * "U17", "U19" oder '' ("None" entfernt den Schlüssel). Schreibt über supabase/players.js feldgenau auf den frischen
   * Datenblock der Datenbank, nicht aus dem Speicher — so überschreibt die Mehrfachzuordnung nichts anderes.
   * Wirft 'notAllowed' (ohne Recht / Staff-Ansicht) vor jedem Schreiben. Gibt { changed, failed } zurück:
   * changed = Zeilen, die die Datenbank tatsächlich geändert hat; failed = markiert, aber nicht geändert.
   */
  async function setAgeGroup(ids, value) {
    const wanted = [...new Set(ids || [])].filter((id) => typeof id === 'string' || typeof id === 'number');
    const v = isAgeGroup(value) ? value : '';
    if (value && !v) throw new Error('invalidAgeGroup');
    if (!can('players', 'write')) throw new Error('notAllowed');
    /* Nicht gespeicherte (lokale) und unvollständige Objekte (Gast-/Staff-Ansicht) nie anfassen */
    const targets = players.value.filter(
      (p) => wanted.includes(p.id) && p._isNew !== true && !p._publicOnly && !p._staffView
    );
    if (!targets.length) return { changed: 0, failed: wanted.length };
    const done = await api.setAgeGroupFor(targets.map((p) => p.id), v);
    for (const p of players.value) if (done.includes(p.id)) p.ageGroup = v;
    return { changed: done.length, failed: wanted.length - done.length };
  }

  /**
   * Spieler endgültig löschen (nur admin; die Datenbank erzwingt es). Läuft über
   * domain/playerDeletion.js: erst sammeln, dann die Zeile (mit Verträgen und Transferhistorie),
   * dann Dateien und Meldungen. `confirmed` muss true sein — die Rückfrage stellt die Oberfläche
   * (SectorPass → DeletePlayerButton), hier gibt es keinen Weg ohne.
   *
   * Wirft vor dem Löschen der Zeile ('notAllowed', 'loadFailed', 'deleteFailed'): dann ist nichts
   * verändert. Nach dem Löschen wirft nichts mehr; das Ergebnis nennt Dateien, die liegen blieben.
   * Die Liste wird danach lokal aktualisiert, die Akte schließt. Löst keinen Push aus.
   */
  async function remove(player, { confirmed } = {}) {
    if (!can('players', 'write') || role.value !== 'admin') throw new Error('notAllowed');
    const result = await deletePlayerFlow({
      player,
      confirmed,
      others: players.value.filter((p) => p.id !== player.id),
      io: {
        collect: async (id) => {
          const [contractFilePaths, transfers, listed] = await Promise.all([
            contractsApi.listContractFilePaths(id),
            transfersApi.listTransfers(id),
            storageApi.listPlayerFiles(id, NON_PLAYER_FOLDERS),
          ]);
          return { contractFilePaths, transfers, listed };
        },
        foreignLogoRefs: (paths, id) => transfersApi.foreignLogoRefs(paths, id),
        /* Logos, die das Klubregister nennt, werden nie gelöscht; schlägt die Abfrage fehl, wird nichts gelöscht */
        registerLogoRefs: (paths) => clubsApi.registerLogoRefs(paths),
        deleteRow: (id) => api.deletePlayer(id),
        removeFiles: (paths) => storageApi.removeFiles(paths),
        deleteNotifications: (id) => notificationsApi.deleteNotificationsForPlayer(id),
      },
    });

    players.value = players.value.filter((p) => p.id !== player.id);
    if (openPlayerId.value === player.id) {
      openPlayerId.value = null;
      profileContext.value = null;
    }
    clearUploads(player.id);
    useNotifications().dropForPlayer(player.id);
    return result;
  }

  /** Neuer Spieler — nur im Speicher, bis save() ihn anlegt. */
  function createDraft() {
    const draft = ensurePlayerShape({
      _isNew: true,
      status: 'trial',
      addedDate: todayIso(), // lokales Datum, nicht UTC (siehe domain/birthDate.js)
    });
    /* Wer Vertragsdaten sehen darf, bekommt auch beim neuen Spieler ein leeres
       Formular — sonst gäbe es keinen Ort für die Eingabe. */
    if (contractsAllowed() && !contractsError.value) attachContract(draft, null, []);
    return draft;
  }

  /* `context`: IDs der Liste, aus der geöffnet wird, in angezeigter Reihenfolge (optional). Ohne sie gibt es im
     Profil keinen Wechsel zum vorherigen/nächsten Spieler. */
  function open(id, tab = null, context = null) {
    openPlayerId.value = id;
    requestedTab.value = tab;
    profileContext.value = makeContext(context);
  }
  function close() {
    openPlayerId.value = null;
    profileContext.value = null;
  }

  /* Verfügbar = in der geladenen Liste (gelöschte oder nicht mehr lesbare Spieler fehlen dort) */
  const isLoaded = (id) => players.value.some((p) => p.id === id);
  const profilePosition = computed(() => positionOf(profileContext.value, openPlayerId.value, isLoaded));
  const canStepProfileBy = (delta) => canStepProfile(profileContext.value, openPlayerId.value, delta, isLoaded);

  /**
   * Zum vorherigen (-1) / nächsten (+1) Spieler des Listenkontexts. Der Kontext bleibt unverändert, der
   * Reiter wird nicht vorgegeben (das Panel springt auf Player Info). Nicht ladbare Spieler werden übersprungen.
   * @returns {{ id: *, skipped: number } | null} null = am Ende der Liste
   */
  function openAdjacent(delta) {
    const step = resolveStep(profileContext.value, openPlayerId.value, delta, isLoaded);
    if (!step) return null;
    openPlayerId.value = step.id;
    requestedTab.value = null;
    return step;
  }

  return {
    players, loading, error, query,
    matching, squad, filteredSquad, squadSections, transferred, transferredMatching, released,
    transferView,
    positionFilter, sortMode, positionCounts,
    yearFilter, activeYear, years, yearCounts, ageDirection,
    ageGroupFilter, ageGroupCounts: ageGroupCountsNow, anyAgeGroup, setAgeGroup, activeFilterCount, resetFilters,
    openPlayer, openPlayerId, requestedTab,
    profileContext, profilePosition, canStepProfileBy, openAdjacent,
    load, loadOnce, reset, syncSource, refreshForReader, save, remove, createDraft, open, close,
    contractsError, syncContracts,
  };
}
